import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Virtual Table Grouping Showcase
 * Demonstrates grouping and summary features with virtual scrolling
 */
import { useState, useMemo } from 'react';
import { VirtualDataTable } from '../../components/data/virtual-data-table';
import { Button } from '../../components/button';
import { cn } from '../../utils/cn';
// Generate sales data for grouping demo
const generateSalesData = (count) => {
    const regions = ['North America', 'Europe', 'Asia', 'South America'];
    const countries = {
        'North America': ['USA', 'Canada', 'Mexico'],
        'Europe': ['UK', 'Germany', 'France', 'Spain'],
        'Asia': ['China', 'Japan', 'India', 'South Korea'],
        'South America': ['Brazil', 'Argentina', 'Chile']
    };
    const products = ['Laptop', 'Phone', 'Tablet', 'Monitor', 'Keyboard', 'Mouse'];
    const quarters = ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024'];
    return Array.from({ length: count }, (_, i) => {
        const region = regions[Math.floor(Math.random() * regions.length)];
        const countryList = countries[region];
        const country = countryList[Math.floor(Math.random() * countryList.length)];
        return {
            id: i + 1,
            region,
            country,
            product: products[Math.floor(Math.random() * products.length)],
            quarter: quarters[Math.floor(Math.random() * quarters.length)],
            salesperson: `Sales Rep ${String((i % 20) + 1).padStart(2, '0')}`,
            quantity: Math.floor(Math.random() * 100) + 10,
            unitPrice: Math.floor(Math.random() * 1000) + 100,
            revenue: 0, // Will be calculated
            discount: Math.floor(Math.random() * 20),
            tax: Math.floor(Math.random() * 10) + 5,
            profit: 0 // Will be calculated
        };
    }).map(item => ({
        ...item,
        revenue: item.quantity * item.unitPrice,
        profit: (item.quantity * item.unitPrice) * (1 - item.discount / 100) * (1 - item.tax / 100)
    }));
};
// Table columns with formatters and resizing
const columns = [
    {
        key: 'id',
        label: 'ID',
        sortable: true,
        defaultWidth: 60,
        minWidth: '50px',
        maxWidth: '100px',
        resizable: true,
        align: 'center'
    },
    {
        key: 'region',
        label: 'Region',
        sortable: true,
        defaultWidth: 150,
        minWidth: '120px',
        maxWidth: '200px',
        resizable: true,
        render: (value) => (_jsx("span", { className: "font-semibold text-theme-accent", title: value, children: value }))
    },
    {
        key: 'country',
        label: 'Country',
        sortable: true,
        defaultWidth: 120,
        minWidth: '100px',
        maxWidth: '160px',
        resizable: true
    },
    {
        key: 'product',
        label: 'Product',
        sortable: true,
        defaultWidth: 120,
        minWidth: '100px',
        maxWidth: '150px',
        resizable: true,
        render: (value) => (_jsx("span", { className: cn("px-2 py-1 rounded text-xs font-medium", value === 'Laptop' && "bg-blue-100 text-blue-800", value === 'Phone' && "bg-green-100 text-green-800", value === 'Tablet' && "bg-purple-100 text-purple-800", value === 'Monitor' && "bg-yellow-100 text-yellow-800", value === 'Keyboard' && "bg-red-100 text-red-800", value === 'Mouse' && "bg-gray-100 text-gray-800"), title: value, children: value }))
    },
    {
        key: 'quarter',
        label: 'Quarter',
        sortable: true,
        defaultWidth: 90,
        minWidth: '80px',
        maxWidth: '120px',
        resizable: true
    },
    {
        key: 'salesperson',
        label: 'Sales Person',
        sortable: true,
        defaultWidth: 140,
        minWidth: '120px',
        maxWidth: '180px',
        resizable: true
    },
    {
        key: 'quantity',
        label: 'Qty',
        sortable: true,
        defaultWidth: 80,
        minWidth: '60px',
        maxWidth: '100px',
        resizable: true,
        align: 'right',
        render: (value) => value.toLocaleString()
    },
    {
        key: 'unitPrice',
        label: 'Unit Price',
        sortable: true,
        defaultWidth: 110,
        minWidth: '90px',
        maxWidth: '140px',
        resizable: true,
        align: 'right',
        render: (value) => `$${value.toLocaleString()}`
    },
    {
        key: 'revenue',
        label: 'Revenue',
        sortable: true,
        defaultWidth: 130,
        minWidth: '100px',
        maxWidth: '160px',
        resizable: true,
        align: 'right',
        render: (value) => (_jsxs("span", { className: "font-semibold text-green-600", children: ["$", value.toLocaleString()] }))
    },
    {
        key: 'discount',
        label: 'Discount',
        sortable: true,
        defaultWidth: 90,
        minWidth: '70px',
        maxWidth: '110px',
        resizable: true,
        align: 'right',
        render: (value) => `${value}%`
    },
    {
        key: 'profit',
        label: 'Profit',
        sortable: true,
        defaultWidth: 130,
        minWidth: '100px',
        maxWidth: '160px',
        resizable: true,
        align: 'right',
        render: (value) => (_jsxs("span", { className: "font-semibold text-blue-600", children: ["$", value.toLocaleString()] }))
    }
];
// Grouping configurations
const groupingPresets = {
    none: {
        label: 'No Grouping',
        config: []
    },
    byRegion: {
        label: 'Group by Region',
        config: [
            {
                field: 'region',
                label: 'Region',
                formatter: (value) => `📍 ${value}`
            }
        ]
    },
    byRegionCountry: {
        label: 'Group by Region → Country',
        config: [
            {
                field: 'region',
                label: 'Region',
                formatter: (value) => `🌍 ${value}`
            },
            {
                field: 'country',
                label: 'Country',
                formatter: (value) => `📍 ${value}`
            }
        ]
    },
    byProduct: {
        label: 'Group by Product',
        config: [
            {
                field: 'product',
                label: 'Product Category',
                formatter: (value) => `📦 ${value}`
            }
        ]
    },
    byQuarter: {
        label: 'Group by Quarter',
        config: [
            {
                field: 'quarter',
                label: 'Quarter',
                formatter: (value) => `📅 ${value}`
            }
        ]
    },
    byQuarterRegion: {
        label: 'Group by Quarter → Region',
        config: [
            {
                field: 'quarter',
                label: 'Quarter',
                formatter: (value) => `📅 ${value}`
            },
            {
                field: 'region',
                label: 'Region',
                formatter: (value) => `🌍 ${value}`
            }
        ]
    },
    complex: {
        label: 'Complex (Region → Country → Product)',
        config: [
            {
                field: 'region',
                label: 'Region',
                formatter: (value) => `🌍 ${value}`
            },
            {
                field: 'country',
                label: 'Country',
                formatter: (value) => `📍 ${value}`
            },
            {
                field: 'product',
                label: 'Product',
                formatter: (value) => `📦 ${value}`
            }
        ]
    }
};
// Bulk actions for demo
const bulkActions = [
    {
        key: 'export',
        label: 'Export Selected',
        icon: _jsx("span", { children: "\uD83D\uDCCA" }),
        variant: 'outline',
        onClick: (selectedItems) => {
            alert(`Exporting ${selectedItems.length} selected items`);
        }
    },
    {
        key: 'delete',
        label: 'Delete Selected',
        icon: _jsx("span", { children: "\uD83D\uDDD1\uFE0F" }),
        variant: 'destructive',
        onClick: (selectedItems) => {
            if (confirm(`Delete ${selectedItems.length} selected items?`)) {
                alert('Items deleted (demo)');
            }
        }
    },
    {
        key: 'duplicate',
        label: 'Duplicate',
        icon: _jsx("span", { children: "\uD83D\uDCCB" }),
        variant: 'default',
        onClick: (selectedItems) => {
            alert(`Duplicating ${selectedItems.length} items`);
        }
    }
];
// Filters for demo
const filters = [
    {
        key: 'region',
        label: 'Region',
        type: 'select',
        options: [
            { value: 'North America', label: 'North America' },
            { value: 'Europe', label: 'Europe' },
            { value: 'Asia', label: 'Asia' },
            { value: 'South America', label: 'South America' }
        ]
    },
    {
        key: 'product',
        label: 'Product',
        type: 'select',
        options: [
            { value: 'Laptop', label: 'Laptop' },
            { value: 'Phone', label: 'Phone' },
            { value: 'Tablet', label: 'Tablet' },
            { value: 'Monitor', label: 'Monitor' },
            { value: 'Keyboard', label: 'Keyboard' },
            { value: 'Mouse', label: 'Mouse' }
        ]
    },
    {
        key: 'quarter',
        label: 'Quarter',
        type: 'select',
        options: [
            { value: 'Q1 2024', label: 'Q1 2024' },
            { value: 'Q2 2024', label: 'Q2 2024' },
            { value: 'Q3 2024', label: 'Q3 2024' },
            { value: 'Q4 2024', label: 'Q4 2024' }
        ]
    },
    {
        key: 'minRevenue',
        label: 'Minimum Revenue',
        type: 'text',
        placeholder: 'Enter minimum revenue'
    }
];
// Summary configurations
const summaryPresets = {
    none: {
        label: 'No Summary',
        config: []
    },
    basic: {
        label: 'Basic Totals',
        config: [
            {
                field: 'quantity',
                type: 'sum',
                label: 'Total Qty',
                formatter: (value) => value.toLocaleString()
            },
            {
                field: 'revenue',
                type: 'sum',
                label: 'Total Revenue',
                formatter: (value) => `$${value.toLocaleString()}`
            },
            {
                field: 'profit',
                type: 'sum',
                label: 'Total Profit',
                formatter: (value) => `$${value.toLocaleString()}`
            }
        ]
    },
    detailed: {
        label: 'Detailed Summary',
        config: [
            {
                field: 'quantity',
                type: 'sum',
                label: 'Total Qty',
                formatter: (value) => value.toLocaleString()
            },
            {
                field: 'quantity',
                type: 'avg',
                label: 'Avg Qty',
                formatter: (value) => value.toFixed(1)
            },
            {
                field: 'revenue',
                type: 'sum',
                label: 'Total Revenue',
                formatter: (value) => `$${value.toLocaleString()}`
            },
            {
                field: 'revenue',
                type: 'avg',
                label: 'Avg Revenue',
                formatter: (value) => `$${value.toFixed(0)}`
            },
            {
                field: 'profit',
                type: 'sum',
                label: 'Total Profit',
                formatter: (value) => `$${value.toLocaleString()}`
            },
            {
                field: 'discount',
                type: 'avg',
                label: 'Avg Discount',
                formatter: (value) => `${value.toFixed(1)}%`
            }
        ]
    },
    count: {
        label: 'Count Summary',
        config: [
            {
                field: 'id',
                type: 'count',
                label: 'Total Records',
                formatter: (value) => `${value} items`
            },
            {
                field: 'salesperson',
                type: 'count',
                label: 'Sales Reps',
                formatter: (value) => `${value} reps`
            }
        ]
    }
};
export function VirtualTableGroupingShowcase() {
    const [datasetSize, setDatasetSize] = useState(500);
    const [selectedGrouping, setSelectedGrouping] = useState('byRegion');
    const [selectedSummary, setSelectedSummary] = useState('basic');
    const [showSummaryRows, setShowSummaryRows] = useState(true);
    // Generate data
    const data = useMemo(() => generateSalesData(datasetSize), [datasetSize]);
    // Get current configurations
    const currentGrouping = groupingPresets[selectedGrouping];
    const currentSummary = summaryPresets[selectedSummary];
    return (_jsxs("div", { className: "p-6 space-y-6", children: [_jsxs("div", { className: "text-center space-y-2", children: [_jsx("h1", { className: "text-3xl font-bold text-theme-foreground", children: "Data Grouping & Summary Showcase" }), _jsx("p", { className: "text-theme-muted", children: "Demonstrates multi-level grouping, aggregation, and summary rows with virtual scrolling" })] }), _jsxs("div", { className: "bg-theme-secondary/20 p-4 rounded-lg border border-theme-border space-y-4", children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsx("span", { className: "text-sm font-medium w-32", children: "Dataset Size:" }), _jsx("div", { className: "flex gap-2", children: [100, 500, 1000, 5000].map(size => (_jsxs(Button, { size: "sm", variant: datasetSize === size ? 'default' : 'outline', onClick: () => setDatasetSize(size), children: [size.toLocaleString(), " rows"] }, size))) })] }), _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("span", { className: "text-sm font-medium w-32", children: "Grouping:" }), _jsx("div", { className: "flex flex-wrap gap-2", children: Object.entries(groupingPresets).map(([key, preset]) => (_jsx(Button, { size: "sm", variant: selectedGrouping === key ? 'default' : 'outline', onClick: () => setSelectedGrouping(key), children: preset.label }, key))) })] }), _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("span", { className: "text-sm font-medium w-32", children: "Summary:" }), _jsx("div", { className: "flex gap-2", children: Object.entries(summaryPresets).map(([key, preset]) => (_jsx(Button, { size: "sm", variant: selectedSummary === key ? 'default' : 'outline', onClick: () => setSelectedSummary(key), children: preset.label }, key))) })] }), _jsxs("div", { className: "flex items-center gap-4", children: [_jsx("span", { className: "text-sm font-medium w-32", children: "Summary Rows:" }), _jsx(Button, { size: "sm", variant: showSummaryRows ? 'default' : 'outline', onClick: () => setShowSummaryRows(!showSummaryRows), children: showSummaryRows ? '✅ Enabled' : '❌ Disabled' })] }), _jsxs("div", { className: "text-sm text-theme-muted bg-theme-primary/50 p-3 rounded", children: [_jsxs("p", { children: ["\uD83D\uDCA1 ", _jsx("strong", { children: "Tips:" })] }), _jsxs("ul", { className: "list-disc list-inside mt-1 space-y-1", children: [_jsx("li", { children: "Click on group headers to expand/collapse groups" }), _jsx("li", { children: "Summary values appear directly in group header rows" }), _jsx("li", { children: "Multi-level grouping creates nested hierarchies" }), _jsx("li", { children: "Drag column borders to resize columns" }), _jsx("li", { children: "All grouping operations work seamlessly with virtual scrolling" })] })] })] }), _jsxs("div", { className: "bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800", children: [_jsx("h3", { className: "font-semibold mb-2 text-blue-900 dark:text-blue-100", children: "Current Configuration:" }), _jsxs("div", { className: "grid grid-cols-2 gap-4 text-sm", children: [_jsxs("div", { children: [_jsx("strong", { children: "Grouping:" }), currentGrouping.config.length > 0 ? (_jsx("ul", { className: "mt-1 space-y-1", children: currentGrouping.config.map((group, idx) => (_jsxs("li", { children: ["Level ", idx + 1, ": ", group.label || group.field] }, idx))) })) : (_jsx("span", { className: "text-theme-muted", children: " None" }))] }), _jsxs("div", { children: [_jsx("strong", { children: "Summary Calculations:" }), currentSummary.config.length > 0 && showSummaryRows ? (_jsx("ul", { className: "mt-1 space-y-1", children: currentSummary.config.map((summary, idx) => (_jsxs("li", { children: [summary.label, ": ", summary.type, " of ", summary.field] }, idx))) })) : (_jsx("span", { className: "text-theme-muted", children: " None" }))] })] })] }), _jsx("div", { className: "space-y-4", children: _jsx(VirtualDataTable, { title: "Sales Analytics Dashboard", description: `Analyzing ${data.length.toLocaleString()} sales records with grouping and summaries`, data: data, columns: columns, grouping: currentGrouping.config.length > 0 ? {
                        enabled: true,
                        groups: currentGrouping.config,
                        summaryConfig: showSummaryRows ? currentSummary.config : [],
                        defaultExpanded: false,
                        showGroupSummary: showSummaryRows,
                        showGrandSummary: showSummaryRows
                    } : undefined, selectable: true, searchable: true, sortable: true, bulkActions: bulkActions, filters: filters, height: "600px", virtualScrolling: true, className: "border-2 border-theme-accent/20" }) }), _jsxs("div", { className: "bg-theme-secondary/10 p-4 rounded-lg border border-theme-border", children: [_jsx("h3", { className: "font-semibold mb-3", children: "\u2728 Feature Highlights" }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm", children: [_jsxs("div", { className: "bg-theme-primary p-3 rounded", children: [_jsx("h4", { className: "font-medium text-theme-accent mb-1", children: "\uD83C\uDFAF Multi-Level Grouping" }), _jsx("p", { className: "text-theme-muted", children: "Group data by multiple fields creating nested hierarchies (e.g., Region \u2192 Country \u2192 Product)" })] }), _jsxs("div", { className: "bg-theme-primary p-3 rounded", children: [_jsx("h4", { className: "font-medium text-theme-accent mb-1", children: "\uD83D\uDCCA Summary Calculations" }), _jsx("p", { className: "text-theme-muted", children: "Automatic aggregations including sum, average, count, min, and max for each group" })] }), _jsxs("div", { className: "bg-theme-primary p-3 rounded", children: [_jsx("h4", { className: "font-medium text-theme-accent mb-1", children: "\uD83D\uDE80 Virtual Performance" }), _jsx("p", { className: "text-theme-muted", children: "Handles thousands of grouped rows with smooth 60fps scrolling performance" })] }), _jsxs("div", { className: "bg-theme-primary p-3 rounded", children: [_jsx("h4", { className: "font-medium text-theme-accent mb-1", children: "\uD83C\uDFA8 Custom Formatting" }), _jsx("p", { className: "text-theme-muted", children: "Format group headers and summary values with custom functions" })] }), _jsxs("div", { className: "bg-theme-primary p-3 rounded", children: [_jsx("h4", { className: "font-medium text-theme-accent mb-1", children: "\uD83D\uDD04 Dynamic Updates" }), _jsx("p", { className: "text-theme-muted", children: "Change grouping and summary configurations on the fly without losing state" })] }), _jsxs("div", { className: "bg-theme-primary p-3 rounded", children: [_jsx("h4", { className: "font-medium text-theme-accent mb-1", children: "\uD83D\uDCF1 Responsive Design" }), _jsx("p", { className: "text-theme-muted", children: "Works seamlessly across all screen sizes with proper indentation" })] }), _jsxs("div", { className: "bg-theme-primary p-3 rounded", children: [_jsx("h4", { className: "font-medium text-theme-accent mb-1", children: "\u2194\uFE0F Column Resizing" }), _jsx("p", { className: "text-theme-muted", children: "Drag column borders to resize, with min/max constraints and smooth interaction" })] })] })] }), _jsxs("div", { className: "bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto", children: [_jsx("h3", { className: "font-semibold mb-3 text-green-400", children: "\uD83D\uDCDD Usage Example:" }), _jsx("pre", { className: "text-sm", children: _jsx("code", { children: `// Column configuration with resizing
const columns = [
  {
    key: 'region',
    label: 'Region',
    defaultWidth: 150,     // Default width in pixels
    minWidth: '120px',     // Minimum width
    maxWidth: '200px',     // Maximum width
    resizable: true,       // Enable resizing (default: true)
    sortable: true
  },
  {
    key: 'revenue',
    label: 'Revenue',
    defaultWidth: 130,
    minWidth: '100px',
    maxWidth: '160px',
    resizable: true,
    render: (value) => \`$\${value.toLocaleString()}\`
  }
];

// Grouping configuration
const groupingConfig = {
  enabled: true,
  groups: [
    { field: 'region', formatter: (value) => \`🌍 \${value}\` },
    { field: 'country' }
  ],
  summaryConfig: [
    { field: 'revenue', type: 'sum', formatter: (v) => \`$\${v.toLocaleString()}\` },
    { field: 'quantity', type: 'avg' }
  ],
  showGroupSummary: true,  // Show summaries in group headers
  showGrandSummary: true   // Show grand total footer
};

// Use in VirtualDataTable
<VirtualDataTable
  data={salesData}
  columns={columns}          // Resizable columns
  grouping={groupingConfig}  // Grouping with summaries
  height="600px"
/>` }) })] })] }));
}
